// src/pages/markets/board/card/TraderListPanel.tsx

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Link, Popper, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { BoardScore, TraderScore } from '../api/boardTypes';
import { useTraders } from '../api/useTraders';
import {
    EM_DASH,
    formatAge,
    formatDuration,
    formatProb,
    formatProbPoints,
    formatRatio,
    formatUsd,
    shortAddress,
    formatCents,
} from './cardFormat';
import { sideLabels, type SideLabels } from '../utils/boardOutcomeLabels';
import { sideColor } from '../utils/sideColor';
import { OUTCOME_MAXLEN_BADGE } from '../../utils/outcomeLabels';
import { toolbarButtonSx } from '../../../../components/common/page/toolbarStyles';
import {
    PANEL_ROW_HEIGHT,
    PanelBody,
    PanelFoot,
    PanelNote,
    panelCellSx,
    panelFrameSx,
    panelRowSx,
    panelTableHeadSx,
} from './panelParts';

const ROW_HOLD_MS = 150;
const ROW_HEIGHT = PANEL_ROW_HEIGHT;
const MAX_ROWS_VISIBLE = 12;
// Rendering every trader freezes the hover; the payload is exposure-sorted already.
const ROW_CAP = 100;
// The side column holds YES/NO at 36px; a named outcome needs room to read.
const SIDE_MAXLEN = 10;
const SIDE_COL_WIDE = 86;
const COL_GAP = 1;
export const TRADER_ROW_TESTID = 'trader-row';

export interface TraderListPanelProps {
    marketId: string | null;
    group: string | null;
    score: BoardScore | null;
    /** The market's true outcome pair, when /api/event-markets has landed. */
    outcomes?: string[] | null;
    now: number;
    traderHref: (address: string) => string;
    rowDetail?: (t: TraderScore) => React.ReactNode;
    detailPlacement?: 'left' | 'right';
}

interface Col {
    key: string;
    label: string;
    width: number;
    align: 'left' | 'right';
    value: (t: TraderScore, now: number) => string;
}

const COLS: Col[] = [
    { key: 'trader', label: 'Trader', width: 86, align: 'left', value: t => shortAddress(t.address) },
    { key: 'side', label: 'Side', width: 36, align: 'left', value: t => t.side },
    { key: 'exp', label: 'Exposure', width: 64, align: 'right', value: t => formatUsd(t.net_exposure_usd) },
    { key: 'held', label: 'Held', width: 42, align: 'right', value: t => formatDuration(t.position_age_seconds) },
    { key: 'chg', label: 'Changed', width: 58, align: 'right', value: (t, now) => formatAge(t.last_position_change_at, now) },
    { key: 'edge', label: 'Edge', width: 56, align: 'right', value: t => (t.avg_edge === null ? EM_DASH : formatProbPoints(t.avg_edge, true)) },
    { key: 'win', label: 'Win', width: 40, align: 'right', value: t => (t.win_rate === null ? EM_DASH : formatProb(t.win_rate)) },
    { key: 'roct', label: 'ROCT', width: 40, align: 'right', value: t => (t.roct === null ? EM_DASH : formatRatio(t.roct)) },
    { key: 'closed', label: 'Closed', width: 44, align: 'right', value: t => String(t.closed_count) },
    {
        key: 'entry', label: 'Entry', width: 42, align: 'right',
        value: t => (t.avg_entry_price === null ? EM_DASH : formatCents(t.avg_entry_price))
    },
];

function gridTemplate(cols: Col[]): string {
    return cols.map(c => `minmax(0, ${c.width}fr)`).join(' ');
}

function stopCardNav(e: React.SyntheticEvent) {
    e.stopPropagation();
}

function Cell({ col, children, bold }: { col: Col; children: React.ReactNode; bold?: boolean }) {
    return (
        <Typography
            component="span"
            sx={{
                ...panelCellSx,
                minWidth: 0,
                fontWeight: bold ? 700 : 400,
                textAlign: col.align,
            }}
        >
            {children}
        </Typography>
    );
}

const TraderRow = memo(function TraderRow({
    t,
    now,
    cols,
    labels,
    traderHref,
    hasDetail,
    active,
    onEnter,
    onLeave,
    onPin,
}: {
    t: TraderScore;
    now: number;
    cols: Col[];
    labels: SideLabels;
    traderHref: (address: string) => string;
    hasDetail: boolean;
    active: boolean;
    onEnter: (address: string, el: HTMLElement) => void;
    onLeave: (address: string) => void;
    onPin: (address: string, el: HTMLElement) => void;
}) {
    const theme = useTheme();
    const side = t.side === 'NO' ? labels.no : labels.yes;
    const rowSideColor = sideColor(labels, t.side === 'NO' ? 'NO' : 'YES', theme);

    return (
        <Box
            data-testid={TRADER_ROW_TESTID}
            onMouseEnter={e => hasDetail && onEnter(t.address, e.currentTarget)}
            onMouseLeave={() => hasDetail && onLeave(t.address)}
            onClick={e => {
                stopCardNav(e);
                e.preventDefault();
                if (hasDetail) onPin(t.address, e.currentTarget);
            }}
            sx={{
                ...panelRowSx,
                display: 'grid',
                gridTemplateColumns: gridTemplate(cols),
                columnGap: COL_GAP,
                cursor: hasDetail ? 'pointer' : 'default',
                bgcolor: active ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.06) },
            }}
        >
            {cols.map(col => {
                if (col.key === 'trader') {
                    return (
                        <Cell key={col.key} col={col} bold>
                            <Link
                                href={traderHref(t.address)}
                                onClick={stopCardNav}
                                underline="hover"
                                sx={{ color: 'text.primary' }}
                            >
                                {col.value(t, now)}
                            </Link>
                        </Cell>
                    );
                }
                if (col.key === 'side') {
                    return (
                        <Typography
                            key={col.key}
                            component="span"
                            title={side.title}
                            sx={{
                                ...panelCellSx,
                                minWidth: 0,
                                fontWeight: 700,
                                color: rowSideColor,
                            }}
                        >
                            {side.text}
                        </Typography>
                    );
                }
                return (
                    <Cell key={col.key} col={col} bold={col.key === 'exp'}>
                        {col.value(t, now)}
                    </Cell>
                );
            })}
        </Box>
    );
});

export function TraderListPanel({
    marketId,
    group,
    score,
    outcomes,
    now,
    traderHref,
    rowDetail,
    detailPlacement = 'left',
}: TraderListPanelProps) {
    const traders = useTraders(marketId, group);
    // The rows are a dense grid; the header line above them has room for more.
    // Memoised: these are passed to every row, so a fresh object each render
    // would defeat TraderRow's memo and re-render the whole list on any tick.
    const labels = useMemo(
        () => sideLabels(score, SIDE_MAXLEN, { outcomes }),
        [score, outcomes]
    );
    const headerLabels = useMemo(
        () => sideLabels(score, OUTCOME_MAXLEN_BADGE, { outcomes }),
        [score, outcomes]
    );
    // A named side needs a wider column than YES/NO; the header shares the widths.
    const cols = useMemo(
        () =>
            labels.named
                ? COLS.map(c =>
                      c.key === 'side' ? { ...c, width: SIDE_COL_WIDE } : c
                  )
                : COLS,
        [labels.named]
    );
    const [hoverAddr, setHoverAddr] = useState<string | null>(null);
    const [hoverEl, setHoverEl] = useState<HTMLElement | null>(null);
    const [pinnedAddr, setPinnedAddr] = useState<string | null>(null);
    const [cap, setCap] = useState(ROW_CAP);
    const holdTimer = useRef<number | null>(null);

    const clearHold = () => {
        if (holdTimer.current !== null) {
            window.clearTimeout(holdTimer.current);
            holdTimer.current = null;
        }
    };

    useEffect(() => clearHold, []);

    // The ladder can swap markets on a mounted panel; expansion must not follow.
    // Done in render, not an effect: an effect resets after the stale cap has
    // already painted the previous market's expanded row count.
    const listKey = `${marketId ?? ''}:${group ?? ''}`;
    const [prevListKey, setPrevListKey] = useState(listKey);
    if (listKey !== prevListKey) {
        setPrevListKey(listKey);
        setCap(ROW_CAP);
    }

    const onEnter = useCallback((address: string, el: HTMLElement) => {
        clearHold();
        holdTimer.current = window.setTimeout(() => {
            setHoverAddr(address);
            setHoverEl(el);
        }, ROW_HOLD_MS);
    }, []);

    const onLeave = useCallback((address: string) => {
        clearHold();
        setHoverAddr(prev => (prev === address ? null : prev));
    }, []);

    const onPin = useCallback((address: string, el: HTMLElement) => {
        clearHold();
        setPinnedAddr(prev => (prev === address ? null : address));
        setHoverAddr(address);
        setHoverEl(el);
    }, []);

    useEffect(() => {
        if (!pinnedAddr) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setPinnedAddr(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [pinnedAddr]);

    const activeAddr = pinnedAddr ?? hoverAddr;
    const payload = traders.data?.traders;
    // The endpoint sorts by |exposure| DESC, but the cap makes that order decide
    // which traders are never seen — too load-bearing to inherit untested.
    const ranked = useMemo(
        () =>
            [...(payload ?? [])].sort(
                (a, b) =>
                    Math.abs(b.net_exposure_usd) - Math.abs(a.net_exposure_usd)
            ),
        [payload]
    );
    const rows = ranked.slice(0, cap);
    const hiddenCount = ranked.length - rows.length;
    const activeTrader = activeAddr ? rows.find(t => t.address === activeAddr) ?? null : null;
    const detailOpen = Boolean(rowDetail && activeTrader && hoverEl);

    const total = score ? score.n_traders_yes + score.n_traders_no : ranked.length;

    // A refetch can reorder a pinned trader out of the capped slice; without this
    // the detail popper vanishes but the pin (and its Escape handler) stay set.
    const activeVisible =
        activeAddr === null || rows.some(t => t.address === activeAddr);
    useEffect(() => {
        if (activeVisible) return;
        setPinnedAddr(null);
        setHoverAddr(null);
        setHoverEl(null);
    }, [activeVisible]);
    const ts = traders.data?.ts ?? score?.ts ?? null;

    return (
        <Box
            onClick={e => {
                stopCardNav(e);
                e.preventDefault();
            }}
            onMouseDown={stopCardNav}
            sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}
        >
            <PanelBody sx={{ px: 0.75 }}>
                {traders.error && (
                    <Box sx={{ px: 0.75 }}>
                        <PanelNote color="error.main">Trader list did not load.</PanelNote>
                    </Box>
                )}

                {!traders.data && !traders.error && (
                    <Box sx={{ px: 0.75 }}>
                        <PanelNote>Loading trader list…</PanelNote>
                    </Box>
                )}

                {traders.data && !traders.error && rows.length === 0 && (
                    <Box sx={{ px: 0.75 }}>
                        <PanelNote>No traders to show.</PanelNote>
                    </Box>
                )}

                {rows.length > 0 && (
                    <>
                        <Box
                            sx={{
                                ...panelRowSx,
                                display: 'grid',
                                gridTemplateColumns: gridTemplate(cols),
                                columnGap: COL_GAP,
                            }}
                        >
                            {cols.map(col => (
                                <Typography
                                    key={col.key}
                                    component="span"
                                    sx={{
                                        ...panelTableHeadSx,
                                        minWidth: 0,
                                        textAlign: col.align,
                                    }}
                                >
                                    {col.label}
                                </Typography>
                            ))}
                        </Box>
                        <Box sx={{ maxHeight: ROW_HEIGHT * MAX_ROWS_VISIBLE, overflowY: 'auto' }}>
                            {rows.map(t => (
                                <TraderRow
                                    key={`${t.address}:${t.outcome_index}`}
                                    t={t}
                                    now={now}
                                    cols={cols}
                                    labels={labels}
                                    traderHref={traderHref}
                                    hasDetail={Boolean(rowDetail)}
                                    active={t.address === activeAddr}
                                    onEnter={onEnter}
                                    onLeave={onLeave}
                                    onPin={onPin}
                                />
                            ))}
                        </Box>
                    </>
                )}
            </PanelBody>

            <PanelFoot>
                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: 'text.primary' }}>
                    {rows.length > 0 ? `${rows.length} of ${total}` : `${total}`} traders
                </Typography>
                {score && (
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                        {score.n_traders_yes} {headerLabels.yes.text} · {score.n_traders_no}{' '}
                        {headerLabels.no.text}
                    </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                {traders.isFetching && <CircularProgress size={12} />}
                {ts && (
                    <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                        {formatAge(ts, now)}
                    </Typography>
                )}
                {hiddenCount > 0 && (
                    <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        onClick={e => {
                            stopCardNav(e);
                            e.preventDefault();
                            setCap(c => c + ROW_CAP);
                        }}
                        sx={{ ...toolbarButtonSx, borderColor: 'divider' }}
                    >
                        Show {Math.min(ROW_CAP, hiddenCount)} more
                    </Button>
                )}
                {traders.data && (
                    <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        onClick={e => {
                            stopCardNav(e);
                            e.preventDefault();
                            traders.refetch();
                        }}
                        sx={{ ...toolbarButtonSx, borderColor: 'divider' }}
                    >
                        Refresh
                    </Button>
                )}
            </PanelFoot>

            {rowDetail && (
                <Popper
                    open={detailOpen}
                    anchorEl={hoverEl}
                    placement={detailPlacement === 'left' ? 'left-start' : 'right-start'}
                    sx={{ zIndex: theme => theme.zIndex.tooltip }}
                    onMouseEnter={() => clearHold()}
                    onMouseLeave={() => {
                        if (!pinnedAddr) setHoverAddr(null);
                    }}
                >
                    <Box
                        onClick={e => {
                            stopCardNav(e);
                            e.preventDefault();
                        }}
                        sx={{
                            ...panelFrameSx,
                            p: 1.5,
                            maxWidth: 360,
                        }}
                    >
                        {activeTrader ? rowDetail(activeTrader) : null}
                    </Box>
                </Popper>
            )}
        </Box>
    );
}